// components/ErrorBoundary.tsx - Enhanced error boundary with recovery options
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Warning, ArrowClockwise, House, Broom, CaretDown, Bug } from '@phosphor-icons/react';
import AccessibleButton from './AccessibleButton';
import { captureException } from '../config/sentry';
import logger from '../utils/logger';
interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
  resetKeys?: Array<string | number>;
  resetOnPropsChange?: boolean;
  isolate?: boolean;
  level?: 'page' | 'section' | 'component';
  isAdmin?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  errorCount: number;
}

class ErrorBoundary extends Component<Props, State> {
  private resetTimeoutId: NodeJS.Timeout | null = null;
  private previousResetKeys: Array<string | number> = [];

  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false,
      errorCount: 0
    };
    this.previousResetKeys = props.resetKeys || [];
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { 
      hasError: true, 
      error 
    };
  }

  componentDidUpdate(prevProps: Props) {
    const { resetKeys, resetOnPropsChange } = this.props;
    const { hasError } = this.state;
    
    // Reset on resetKeys change
    if (hasError && resetKeys) {
      const hasResetKeyChanged = resetKeys.some(
        (key, idx) => key !== this.previousResetKeys[idx]
      );
      
      if (hasResetKeyChanged) {
        this.resetErrorBoundary();
        this.previousResetKeys = resetKeys;
      }
    }
    
    // Reset on any props change if enabled
    if (hasError && resetOnPropsChange && prevProps !== this.props) {
      this.resetErrorBoundary();
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Enhanced logging with full error context
    console.group('🔴 ErrorBoundary Caught Error');
    console.error('Error:', error);
    console.error('Error Message:', error.message);
    console.error('Error Stack:', error.stack);
    console.error('Component Stack:', errorInfo.componentStack);
    console.error('Error Count:', this.state.errorCount + 1);
    console.error('Boundary Level:', this.props.level);
    console.groupEnd();

    logger.error('Error caught by boundary:', error, errorInfo);

    // Send error to Sentry
    captureException(error, {
      errorInfo: errorInfo,
      level: this.props.level,
      errorCount: this.state.errorCount
    });

    this.setState(prevState => ({
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));

    // Log to external service in production
    if ((typeof process !== 'undefined' && process.env?.NODE_ENV === 'production') || import.meta.env?.MODE === 'production') {
      this.logErrorToService(error, errorInfo);
    }

    // Auto-recovery attempt after 5 seconds for transient errors
    // Skip auto-recovery for admin users so they can inspect errors
    if (this.state.errorCount < 3 && !this.props.isAdmin) {
      this.resetTimeoutId = setTimeout(() => {
        this.resetErrorBoundary();
      }, 5000);
    }
  }

  componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  logErrorToService = (error: Error, errorInfo: ErrorInfo) => {
    // Placeholder for error logging service
    // In production, integrate with Sentry, LogRocket, etc.
    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };
    
    // Send to logging endpoint
    fetch('/api/log-error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(errorData),
    }).catch(console.error);
  };

  resetErrorBoundary = () => {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
      this.resetTimeoutId = null;
    }
    
    this.props.onReset?.();
    this.setState({ 
      hasError: false, 
      error: undefined,
      errorInfo: undefined
    });
  };

  goHome = () => {
    window.location.href = '/';
  };

  clearCacheAndReload = async () => {
    try {
      // Clear Service Worker caches first
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        console.log('[ErrorBoundary] Cleared all SW caches');
      }

      // Unregister service workers to force fresh install
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(r => r.unregister()));
        console.log('[ErrorBoundary] Unregistered service workers');
      }

      // Clear localStorage (keep auth data)
      const authToken = localStorage.getItem('valulearn_token');
      const authUser = localStorage.getItem('valulearn_user');
      const theme = localStorage.getItem('valulearn_theme');
      localStorage.clear();
      if (authToken) localStorage.setItem('valulearn_token', authToken);
      if (authUser) localStorage.setItem('valulearn_user', authUser);
      if (theme) localStorage.setItem('valulearn_theme', theme);

      // Clear sessionStorage
      sessionStorage.clear();

      // Force hard reload
      window.location.reload();
    } catch (error) {
      console.error('Failed to clear cache:', error);
      // Force reload anyway
      window.location.reload();
    }
  };

  isChunkLoadError = (error?: Error): boolean => {
    if (!error) return false;
    const message = error.message.toLowerCase();
    return (
      message.includes('failed to fetch dynamically imported module') ||
      message.includes('failed to load module script') ||
      message.includes('error loading dynamically imported module') ||
      message.includes('importing a module script failed') ||
      message.includes('loading chunk') ||
      message.includes('chunkloaderror') ||
      (message.includes('failed to fetch') && message.includes('.js'))
    );
  };

  getErrorMessage = (error?: Error): string => {
    if (!error) return 'An unexpected error occurred';

    const message = error.message.toLowerCase();

    // Detect chunk/module loading errors (stale cache after deployment)
    if (this.isChunkLoadError(error)) {
      return 'A network error occurred. Check your internet connection.';
    }

    // Detect localStorage-related errors
    if (message.includes('localstorage') ||
        message.includes('quota') ||
        message.includes('storage') ||
        message.includes('json') ||
        message.includes('parse')) {
      return 'A data storage error occurred. Try clearing your browser cache.';
    }

    // Detect network errors
    if (message.includes('network') ||
        message.includes('fetch') ||
        message.includes('timeout')) {
      return 'A network error occurred. Check your internet connection.';
    }

    // Detect render errors
    if (message.includes('render') ||
        message.includes('component') ||
        message.includes('undefined is not an object') ||
        message.includes('cannot read property')) {
      return 'A component failed to render properly.';
    }

    return 'The application encountered an unexpected error.';
  };

  isDataRelatedError = (error?: Error): boolean => {
    if (!error) return false;

    const message = error.message.toLowerCase();
    const stack = (error.stack || '').toLowerCase();

    // Include chunk errors - they're often resolved by clearing cache
    return this.isChunkLoadError(error) ||
           message.includes('localstorage') ||
           message.includes('json') ||
           message.includes('parse') ||
           message.includes('storage') ||
           stack.includes('localstorage') ||
           stack.includes('analyticsservice') ||
           stack.includes('exportservice');
  };

  render() {
    const { hasError, error, errorInfo, errorCount } = this.state;
    const { fallback, isolate, level = 'component' } = this.props;

    if (hasError) {
      if (fallback) {
        return <>{fallback}</>;
      }

      // Different error UI based on error boundary level
      if (level === 'page') {
        const isDevelopment = (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') || import.meta.env?.MODE === 'development';

        return (
          <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-grey-extra-light via-white to-grey-extra-light dark:from-grey-darker dark:via-grey-darker dark:to-grey-darker p-4">
            <div
              className="w-full max-w-lg bg-white dark:bg-grey-darker rounded-2xl shadow-xl dark:shadow-grey-darker/50 overflow-hidden border border-grey-very-light dark:border-grey"
              role="alert"
              aria-live="assertive"
            >
              {/* Header with accent */}
              <div className="h-1 w-full bg-gradient-to-r from-red via-orange to-red" />

              <div className="p-8">
                {/* Icon */}
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 rounded-full bg-red-very-light dark:bg-red-darker/20 flex items-center justify-center">
                    <Warning
                      weight="duotone"
                      className="w-10 h-10 text-red dark:text-red-light"
                      aria-hidden="true"
                    />
                  </div>
                </div>

                {/* Title */}
                <h1 className="text-2xl font-bold text-grey-darker dark:text-white text-center mb-3">
                  Something Went Wrong
                </h1>

                {/* Description */}
                <p className="text-grey dark:text-grey-light text-center mb-6 leading-relaxed">
                  {this.getErrorMessage(error)}
                </p>

                {/* Contextual hints */}
                {(errorCount > 1 || this.isDataRelatedError(error)) && (
                  <div className="mb-6 p-4 rounded-xl bg-orange-very-light dark:bg-orange-darker/20 border border-orange-light dark:border-orange-darker/40">
                    <div className="flex items-start gap-3">
                      <Broom weight="duotone" className="w-5 h-5 text-orange flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-orange-darker dark:text-orange">
                          {errorCount > 1 ? `Multiple errors detected (${errorCount})` : 'Data-related issue detected'}
                        </p>
                        <p className="text-xs text-orange-darker/70 dark:text-orange/70 mt-1">
                          Clearing your browser cache should resolve this issue.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div className="space-y-3">
                  {/* Primary actions row */}
                  <div className="flex gap-3">
                    <button
                      onClick={this.resetErrorBoundary}
                      className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-blue hover:bg-blue-darker text-white font-medium rounded-xl transition-all duration-200 hover:shadow-lg"
                      aria-label="Try again"
                    >
                      <ArrowClockwise weight="bold" className="w-4 h-4" />
                      <span>Try Again</span>
                    </button>

                    <button
                      onClick={this.goHome}
                      className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-grey-extra-light dark:bg-grey hover:bg-grey-very-light dark:hover:bg-grey-light/20 text-grey-darker dark:text-white font-medium rounded-xl transition-all duration-200 border border-grey-light dark:border-grey"
                      aria-label="Go to home page"
                    >
                      <House weight="duotone" className="w-4 h-4" />
                      <span>Go Home</span>
                    </button>
                  </div>

                  {/* Secondary action */}
                  <button
                    onClick={this.clearCacheAndReload}
                    className="w-full flex items-center justify-center gap-2 py-2.5 px-4 text-orange hover:text-orange-darker dark:text-orange dark:hover:text-orange-light text-sm font-medium transition-colors"
                    aria-label="Clear cache and reload page"
                  >
                    <Broom weight="duotone" className="w-4 h-4" />
                    <span>Clear Cache & Reload</span>
                  </button>
                </div>

                {/* Development error details */}
                {isDevelopment && error && (
                  <details className="mt-6 group">
                    <summary className="flex items-center gap-2 cursor-pointer text-sm font-medium text-grey hover:text-grey-darker dark:text-grey-light dark:hover:text-white transition-colors select-none">
                      <CaretDown weight="bold" className="w-4 h-4 transition-transform group-open:rotate-180" />
                      <Bug weight="duotone" className="w-4 h-4" />
                      <span>Developer Details</span>
                    </summary>
                    <div className="mt-3 p-4 bg-grey-extra-light dark:bg-grey rounded-xl space-y-3 text-left">
                      {/* Error message */}
                      <div>
                        <p className="text-xs font-medium text-grey-darker dark:text-grey-light mb-1 uppercase tracking-wide">Error</p>
                        <code className="block text-xs text-red dark:text-red-light font-mono break-all">
                          {error.message}
                        </code>
                      </div>

                      {/* Stack trace */}
                      <div>
                        <p className="text-xs font-medium text-grey-darker dark:text-grey-light mb-1 uppercase tracking-wide">Stack Trace</p>
                        <pre className="text-xs text-grey dark:text-grey-light font-mono whitespace-pre-wrap break-all max-h-32 overflow-y-auto scrollbar-thin">
                          {error.stack}
                        </pre>
                      </div>

                      {/* Component stack */}
                      {errorInfo && (
                        <div>
                          <p className="text-xs font-medium text-grey-darker dark:text-grey-light mb-1 uppercase tracking-wide">Component Stack</p>
                          <pre className="text-xs text-grey dark:text-grey-light font-mono whitespace-pre-wrap break-all max-h-32 overflow-y-auto scrollbar-thin">
                            {errorInfo.componentStack}
                          </pre>
                        </div>
                      )}
                    </div>
                  </details>
                )}
              </div>
            </div>
          </div>
        );
      }

      // Component-level error (more compact)
      return (
        <div 
          className={`${isolate ? 'border-2 border-red-darker' : ''} p-4 bg-red-very-light dark:bg-red-darker/20 rounded-lg`}
          role="alert"
          aria-live="assertive"
        >
          <p className="text-red-darker dark:text-red-light font-medium mb-2">
            Component Error
          </p>
          <p className="text-sm text-grey dark:text-grey-light mb-3">
            This component encountered an error and cannot be displayed.
          </p>
          <AccessibleButton
            onClick={this.resetErrorBoundary}
            size="small"
            variant="secondary"
            ariaLabel="Retry loading component"
          >
            Retry
          </AccessibleButton>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;